/**
 * This is a wrapper around ./ui/Select that provides the expected focus and
 * keypress behaviors that a Select control *should* have. The Select as
 * currently exported either exports a do-it-all-yourself model or
 * do-nothing-yourself model which are both incompatible with our needs.
 **/

import * as React from 'react';
import {OverlayViewport} from '@packages/systems/ui/overlay';
import {fixedForwardRef} from '@packages/systems/spring';
import {SelectBase, Group, Option} from './Select';
import Hitarea from './Hitarea';
import * as dropdown from './logic/dropdown';
import type {PropertyOrigin} from '@packages/systems/style-legacy/values';
import {DefaultOrigin} from '@packages/systems/style-legacy/values';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

export {Group, Option};

export type Meta = {
  changeType: 'changeStart' | 'change' | 'changeEnd' | 'changeCancel';
  // For compatibility with WebflowNumberInput.OnChangeMeta, but these are
  // actually never provided by WebflowSelect:
  action?: Record<any, any>;
  type?: string;
};
const CHANGE_START = {changeType: 'changeStart'} as const;
const CHANGE = {changeType: 'change'} as const;
const CHANGE_END = {changeType: 'changeEnd'} as const;
const CHANGE_CANCEL = {changeType: 'changeCancel'} as const;

// this should be polymorphic per <Select />
type Props<Value> = {
  id: string;
  disabled?: boolean;
  origin?: PropertyOrigin;
  value: Value;
  onChange: (arg1: Value, arg2: Meta) => void;
  children: React.ReactNode | ((arg1: State<Value>) => React.ReactElement);
  style?: CSSProperties;
  ['aria-label']?: string;
  // should only be passed only if an id is also provided otherwise the ids will conflict
  ['aria-labelledby']?: string;
  ['data-automation-id']?: string;
  menuWidth?: number;
  /**
   * Optional Select button placeholder for when no value is selected.
   * Defaults to "Select..."
   */
  placeholder?: React.ReactNode;
  defaultIsOpen?: boolean;
  forwardedRef?: React.Ref<HTMLElement>;
  shouldUseInheritedViewport?: boolean;
};

type State<Value> = {
  open: boolean;
  value: null | Value;
  preselected: null | Value;
  hasScrolled: boolean;
  scrollView: Record<any, any>;
  origin: PropertyOrigin;
};

const detectKeydownChangeType = (
  open: boolean,
  preselected: unknown,
  value: unknown,
  key: string
) => {
  if (open) {
    // the easist case. If the Select is open, it’s just a change event
    return CHANGE;
  } else if (key === 'Enter' || key === ' ') {
    return CHANGE_END;
  } else if (key === 'Escape' || preselected === value) {
    // it gets a bit awkward here since dropdown.handleKeyDown() resets
    // `preselected` to `null` when the value is changed (e.g. Enter or space)
    // so we need to compare the `preselected` value _before_
    // `dropdown.handleKeyDown` is called with the value afterward to see if we
    // should cancel or current ephemeral update or commit it.
    return CHANGE_CANCEL;
  } else {
    return CHANGE_END;
  }
};

class Select<Value> extends React.PureComponent<Props<Value>, State<Value>> {
  static displayName = 'WebflowSelect';
  state: State<Value> = dropdown.getInitialState({
    value: this.props.value,
    origin: this.props.origin || DefaultOrigin(),
    open: this.props.defaultIsOpen || false,
  });

  getChildren = () =>
    typeof this.props.children === 'function'
      ? this.props.children(this.state)
      : this.props.children;

  onPreselect = (value: Value) =>
    this.setState(
      (state) => dropdown.preselect(state, value),
      value ? () => this.props.onChange(value, CHANGE) : undefined
    );

  onChange = (value: Value) => {
    const {origin, value: startValue} = this.state;
    this.setState({value, open: false}, () => {
      this.props.onChange(
        value,
        (origin && origin.type !== 'local') || startValue !== value
          ? CHANGE_END
          : CHANGE_CANCEL
      );
    });
  };

  onKeyDown = (key: string) => {
    const {preselected} = this.state;
    this.setState(
      (state) =>
        dropdown.handleKeyDown({...state, children: this.getChildren()}, key),
      () => {
        /**
         * Don't trigger a change if pressing Tab and Select is closed.
         * This guard naively checks for Shift + Tab.
         */
        if (!this.state.open && (key === 'Tab' || key === 'Shift')) return;

        const newValue =
          // copying dropdown logic to not breakdown other consumers yayyyy
          key === 'Enter' || key === ' '
            ? preselected
            : this.state.preselected || this.state.value || this.props.value;
        if (newValue) {
          this.props.onChange(
            newValue,
            detectKeydownChangeType(
              this.state.open,
              preselected,
              this.props.value,
              key
            )
          );
        }
      }
    );
  };

  onOpen = () => {
    this.setState(dropdown.open, () => {
      this.props.onChange(this.props.value, CHANGE_START);
    });
  };

  onClose = () => {
    this.setState(dropdown.close, () => {
      const newValue = this.state.value;
      this.props.onChange(
        newValue || this.props.value,
        newValue === this.props.value ? CHANGE_CANCEL : CHANGE_END
      );
    });
  };

  onScroll = (scrollData: Record<any, any>) => {
    this.setState((state) =>
      dropdown.scroll({...state, children: this.getChildren()}, scrollData)
    );
  };

  onBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (this.state.open) {
      if (event.relatedTarget) {
        const relatedTarget: HTMLElement = event.relatedTarget as any;
        const listbox = relatedTarget.closest('[role="listbox"]');
        if (
          listbox &&
          listbox.getAttribute('aria-labelledby') === this.props.id
        ) {
          event.preventDefault();
          return;
        }
      }
      this.setState(dropdown.close, () => {
        this.props.onChange(this.props.value, CHANGE_CANCEL);
      });
    }
  };

  static getDerivedStateFromProps<Value>(
    props: Props<Value>,
    state: State<Value>
  ) {
    const propsOrigin = props.origin || DefaultOrigin();
    // don’t sync when open because that causes weird
    if (
      state.open === false &&
      (props.value !== state.value || propsOrigin !== state.origin)
    ) {
      return {value: props.value, origin: propsOrigin};
    }
    return null;
  }

  componentWillUnmount() {
    if (this.state.open) {
      // cancel any change if we’re unmounting unexpectedly
      this.props.onChange(this.props.value, CHANGE_CANCEL);
    }
  }

  render() {
    const {forwardedRef, shouldUseInheritedViewport, ...props} = this.props;
    const select = (
      <Hitarea ref={forwardedRef}>
        <SelectBase
          data-prevent-global-event-handlers
          {...this.state}
          {...props}
          // @ts-expect-error - TS2322 - Type '{ children: any; onBlur: (event: FocusEvent<HTMLElement, Element>) => void; value: Value | null; animated: false; onOpen: () => void; onClose: () => void; ... 18 more ...; "data-prevent-global-event-handlers": true; }' is not assignable to type 'IntrinsicAttributes & SelectBaseProps'.
          onBlur={this.onBlur}
          value={this.state.value}
          animated={false}
          onOpen={this.onOpen}
          onClose={this.onClose}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
          onPreselect={this.onPreselect}
          onScroll={this.onScroll}
        >
          {this.getChildren()}
        </SelectBase>
      </Hitarea>
    );

    return shouldUseInheritedViewport ? (
      select
    ) : (
      <OverlayViewport>{select}</OverlayViewport>
    );
  }
}

// `fixedForwardRef` preserves the `Value` generic through `forwardRef`
// (React's built-in `forwardRef` typing erases it). Same helper Spring's
// own `Select` uses — see its docstring for the why. The render function is
// named `WebflowSelect` so React DevTools labels the wrapper accordingly.
const SelectPublic = fixedForwardRef(function WebflowSelect<Value>(
  props: Props<Value>,
  ref: React.Ref<HTMLElement>
) {
  return <Select<Value> {...props} forwardedRef={ref} />;
});

export {SelectPublic as Select};

/** @deprecated Use `Select` directly — it is now a `forwardRef`. */
export {SelectPublic as ForwardedSelect};
